// src/components/ListEditor.js
import React, { useEffect, useState } from "react";
import "../styles/Admin.css";
import ImageCropUpload from "./ImageCropUpload";

function uid() {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default function ListEditor({
  title,
  items = [],
  onChange,
  token,
  onAuthError, // ✅ FIX: accept this prop so it’s defined
  fields = [],
  defaultItem = {},
}) {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(defaultItem || {});
  const [descriptionLength, setDescriptionLength] = useState(
    (defaultItem?.description || "").length
  );

  useEffect(() => {
    if (!editingId) {
      setDraft(defaultItem || {});
      setDescriptionLength((defaultItem?.description || "").length);
    }
    // eslint-disable-next-line
  }, [editingId]);

  const startEdit = (item) => {
    setEditingId(item.id);
    setDraft({ ...item });
    setDescriptionLength((item.description || "").length);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(defaultItem || {});
    setDescriptionLength((defaultItem?.description || "").length);
  };

  const saveDraft = () => {
    const next = Array.isArray(items) ? [...items] : [];
    if (editingId) {
      const idx = next.findIndex((x) => x.id === editingId);
      if (idx >= 0) next[idx] = { ...draft, id: editingId };
    } else {
      next.unshift({ ...draft, id: uid() });
    }
    onChange(next);
    cancelEdit();
  };

  const removeItem = (id) => {
    onChange(items.filter((x) => x.id !== id));
  };

  const move = (id, dir) => {
    const idx = items.findIndex((x) => x.id === id);
    if (idx < 0) return;
    const next = [...items];
    const swapWith = idx + dir;
    if (swapWith < 0 || swapWith >= next.length) return;
    const tmp = next[idx];
    next[idx] = next[swapWith];
    next[swapWith] = tmp;
    onChange(next);
  };

  const renderField = (f) => {
    const value = draft?.[f.key] ?? "";
    const maxLength = f.maxLength || null;

    if (f.type === "select") {
      return (
        <div key={f.key}>
          <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>
            {f.label}
          </label>
          <select
            className="admin-input"
            value={value}
            onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
          >
            {(f.options || []).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (f.type === "image") {
      return (
        <div key={f.key}>
          <ImageCropUpload
            token={token}
            onAuthError={onAuthError}
            value={value}
            onChange={(url) => setDraft({ ...draft, [f.key]: url })}
            folder={f.folder || "general"}
            label={f.label}
            aspect={f.aspect || 1}
          />
        </div>
      );
    }

    if (f.type === "file") {
      return (
        <div key={f.key}>
          <ImageCropUpload
            token={token}
            onAuthError={onAuthError}
            value={value}
            onChange={(url) => setDraft({ ...draft, [f.key]: url })}
            folder={f.folder || "reports"}
            label={f.label}
            isPdf={true}
            aspect={f.aspect || 1}
          />
        </div>
      );
    }

    if (f.type === "textarea") {
      return (
        <div key={f.key} style={{ gridColumn: "1 / -1" }}>
          <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>
            {f.label}{" "}
            {maxLength ? `(${descriptionLength}/${maxLength} characters)` : ""}
          </label>
          <textarea
            className="admin-input"
            style={{ minHeight: 100 }}
            value={value}
            onChange={(e) => {
              setDraft({ ...draft, [f.key]: e.target.value });
              if (f.key === "description") setDescriptionLength(e.target.value.length);
            }}
            maxLength={maxLength || undefined}
            placeholder={f.placeholder || ""}
          />
        </div>
      );
    }

    return (
      <div key={f.key}>
        <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>
          {f.label}
        </label>
        <input
          className="admin-input"
          type="text"
          value={value}
          onChange={(e) => {
            setDraft({ ...draft, [f.key]: e.target.value });
            if (f.key === "description") setDescriptionLength(e.target.value.length);
          }}
          maxLength={maxLength || undefined}
          placeholder={f.placeholder || ""}
        />
      </div>
    );
  };

  return (
    <div className="admin-card">
      <h2 style={{ marginTop: 0 }}>{title}</h2>

      <div className="admin-row">{fields.map(renderField)}</div>

      <div className="admin-toolbar">
        <div className="admin-actions">
          <button type="button" className="admin-btn-primary" onClick={saveDraft}>
            {editingId ? "Save Changes" : "Add"}
          </button>
          {editingId ? (
            <button type="button" className="admin-btn-secondary" onClick={cancelEdit}>
              Cancel
            </button>
          ) : null}
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        {items.length ? (
          <div className="admin-list">
            {items.map((item, idx) => (
              <div key={item.id} className="admin-list-row">
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800 }}>
                    {item.title || item.name || item.type || "Item"}{" "}
                    <span style={{ fontWeight: 400, opacity: 0.6 }}>#{idx + 1}</span>
                  </div>
                  {item.description ? (
                    <div style={{ opacity: 0.75, marginTop: 4 }}>
                      {String(item.description).slice(0, 140)}
                      {String(item.description).length > 140 ? "…" : ""}
                    </div>
                  ) : null}
                </div>

                <div className="admin-actions">
                  <button
                    type="button"
                    className="admin-btn-secondary"
                    onClick={() => move(item.id, -1)}
                    disabled={idx === 0}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="admin-btn-secondary"
                    onClick={() => move(item.id, +1)}
                    disabled={idx === items.length - 1}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="admin-btn-secondary"
                    onClick={() => startEdit(item)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="admin-btn-danger"
                    onClick={() => removeItem(item.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="admin-note">No items yet.</p>
        )}
      </div>
    </div>
  );
}
